import { formatFlightDateTime } from '@/lib/disruptions/airport-tz';
import type { TripLeg, TripView } from '@/lib/transport/types';
import type { Reservation } from '@/lib/booking/types';
import type { CareCardRow, DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import { careMoney, sheetT, tripWhen, type PnrPrintKind, type SheetT } from './pnr-sheet-model';

// Which coupons a PNR gets, and what each one says.
//
// Split out from the component that draws them (`pnr-coupon-sheet.tsx`) so the
// rules can be asserted without a DOM — and they are rules, not layout: a coupon
// exists only for a thing the passenger actually has, a pick-up time is never
// invented for a ride that has none, and a balance the wallet did not report is
// never turned into a number.
//
// The shape is deliberately flat — headline, a handful of short fields, one
// instruction — because every one of these ends up alone in someone's hand: the
// hotel coupon on a front desk, the transport coupon with a driver, the flight
// coupon at a counter. Whoever is holding it will not be holding the others.
//
// i18n: this module is not a component, so it holds translation KEYS, never
// language — resolved through the translator passed as the last argument, which
// defaults to `sheetT` (see `pnr-sheet-model.ts`). Copy lives in `pnrActions`
// under `coupon.*`, and the register is the PASSENGER's, not the console's: a
// coupon is read by whoever is holding it, so it is short and plain, and it says
// `boleto`, not `ticket`, in Spanish.
//
// The leg captions are the module's own rather than `TRIP_LEG_LABEL`: the shared
// table is operator vocabulary read on screen, this one is 203 mm of paper in a
// driver's hand, and the two are free to word the same leg differently.

/** How a leg reads on paper, as `pnrActions` keys. */
const LEG_KEY: Record<TripLeg, string> = {
  AIRPORT_TO_HOTEL: 'coupon.transport.leg.airportToHotel',
  HOTEL_TO_AIRPORT: 'coupon.transport.leg.hotelToAirport',
  AIRPORT_TO_HOME: 'coupon.transport.leg.airportToHome',
};

export interface CouponSource {
  group: DisruptionPnrGroupRow;
  kind: PnrPrintKind;
  reservation: Reservation | undefined;
  card: CareCardRow | undefined;
  balancesKnown: boolean;
  careUnavailable: boolean;
  trips: readonly TripView[];
  transportUnavailable: boolean;
  mapsHref: string | undefined;
  /** The people this voucher covers, in full and lead-first — never masked. */
  passengerNames: readonly string[];
  /** The airline this console is pinned to, already upper-cased. */
  tenant: string;
}

export interface CouponField {
  label: string;
  value: string;
}

export interface Coupon {
  id: string;
  /** The strip across the top — what KIND of coupon this is. */
  title: string;
  /** The one fact this coupon exists to state. */
  headline: string;
  sub?: string | undefined;
  fields: CouponField[];
  /** The instruction for whoever ends up holding this coupon alone. */
  note?: string | undefined;
  /** Printed small and wrapped — only where a URL is any use. */
  link?: string | undefined;
}

/**
 * Which coupons this PNR gets, and what each one says.
 *
 * Pure, and exported for its own test: the honesty rules (no invented pick-up
 * time, no balance the wallet did not report, no coupon for a thing the
 * passenger does not have) are decided here, not in JSX, so they can be asserted
 * without a printer or a DOM.
 */
export function buildCoupons(src: CouponSource, t: SheetT = sheetT): Coupon[] {
  const { group, kind, reservation, card, trips, tenant } = src;
  const isPack = kind === 'travel-pack';
  const station = group.overnightStation ?? group.destination;
  const flight = group.assignedFlight;
  const hasFlight = !!flight || !!group.ticketNumber;
  const entitlement = group.mealVoucher;
  const hasCareMoney = !!card || typeof entitlement?.amountUsd === 'number';
  const coupons: Coupon[] = [];

  if (kind === 'flight-voucher' || (isPack && hasFlight)) {
    const origin = flight?.origin ?? group.origin;
    const destination = flight?.destination ?? group.destination;
    const departs = flight?.departureAt ?? group.departureAt;
    coupons.push({
      id: 'flight',
      title: t('coupon.flight.title'),
      headline: flight?.number ?? group.flightNumber ?? '—',
      sub: `${origin ?? '—'} → ${destination ?? '—'}`,
      fields: [
        { label: t('coupon.flight.departs'), value: formatFlightDateTime(departs, origin) },
        { label: t('coupon.flight.cabin'), value: flight?.cabin ?? 'ECONOMY' },
        { label: t('coupon.flight.seat'), value: flight?.seat ?? t('coupon.flight.seatAtCheckIn') },
        {
          label: t('coupon.flight.ticket'),
          value: group.ticketNumber ?? t('coupon.flight.ticketPending'),
        },
      ],
      note: t('coupon.flight.note', { tenant }),
    });
  }

  if ((kind === 'hotel-voucher' || (isPack && !!reservation)) && reservation) {
    const hotel = reservation.hotelName ?? t('coupon.hotel.assignedHotel');
    // The directions ride here on the pack rather than claiming their own piece
    // of stock: nobody reads "how to get there" without the hotel name in the
    // same hand. The standalone directions coupon below still exists for the
    // reprint that is only ever about the route.
    const gettingThere =
      isPack && station ? ` ${t('coupon.hotel.gettingThere', { hotel, station })}` : '';
    coupons.push({
      id: 'hotel',
      title: t('coupon.hotel.title'),
      headline: hotel,
      sub: station ? t('coupon.hotel.overnightAt', { station }) : undefined,
      fields: [
        { label: t('coupon.hotel.rooms'), value: String(reservation.rooms ?? 1) },
        { label: t('coupon.hotel.guests'), value: String(reservation.guests ?? group.paxCount) },
        {
          label: t('coupon.hotel.reference'),
          value: reservation.reservationUrn.split(':').pop() ?? '—',
        },
        { label: t('coupon.hotel.billedTo'), value: tenant },
      ],
      note: `${t('coupon.hotel.note')}${gettingThere}`,
    });
  }

  if (isPack && hasCareMoney) {
    const money = careMoney(card, entitlement, src.balancesKnown, t);
    // A four-column grid on 82 mm of paper truncates: a field labelled
    // "Available" whose value is the SENTENCE "Balance not available right now"
    // prints as "Balance not avail…", which reads like a number that got cut off
    // rather than a balance nobody could read. So the field carries an amount or
    // it does not exist, and the reason moves to the note where it has room.
    const availableAmount =
      card && src.balancesKnown && typeof card.availableMinor === 'number'
        ? money.availableLabel
        : undefined;
    coupons.push({
      id: 'care',
      title: t('coupon.care.title'),
      headline: money.loadedLabel,
      sub: money.cardLabel
        ? t('sheet.care.onCard', { card: money.cardLabel })
        : t('coupon.care.allowanceAssigned'),
      fields: [
        // #808: the PRINTED reference — what the counter keys to validate THIS coupon
        // against the group's card before marking it consumed. First, so it is the
        // one field ticket stock never truncates away.
        ...(card?.reference ? [{ label: t('coupon.care.reference'), value: card.reference }] : []),
        { label: t('sheet.care.loaded'), value: money.loadedLabel },
        ...(availableAmount ? [{ label: t('sheet.care.available'), value: availableAmount }] : []),
        ...(money.cardLabel ? [{ label: t('sheet.care.card'), value: money.cardLabel }] : []),
        ...(card?.pendingTopUp
          ? [{ label: t('coupon.care.topUp'), value: t('coupon.care.topUpOnWay') }]
          : []),
      ],
      note: card
        ? `${t('coupon.care.note')}${card.reference ? ` ${t('coupon.care.presentCode')}` : ''}${availableAmount ? '' : ` ${t('coupon.care.balanceUnread')}`}`
        : src.careUnavailable
          ? t('coupon.care.unreadable')
          : t('coupon.care.notHandedOver'),
    });
  }

  if (isPack) {
    const ownMeans = group.transportOutcome?.outcome === 'OWN_MEANS';
    const transfer = group.transfer;
    if (ownMeans) {
      coupons.push({
        id: 'transport-own',
        title: t('coupon.transport.title'),
        headline: t('coupon.transport.ownMeans'),
        fields: [],
        note: `${t('coupon.transport.ownMeansNote')}${
          group.transportOutcome?.note ? ` ${group.transportOutcome.note}` : ''
        }`,
      });
    } else if (trips.length) {
      for (const trip of trips) {
        coupons.push({
          id: `transport-${trip.urn}`,
          title: t('coupon.transport.title'),
          headline: t(LEG_KEY[trip.leg]),
          sub: tripWhen(trip, station, t),
          fields: [
            ...(trip.driverInfo?.name
              ? [{ label: t('coupon.transport.driver'), value: trip.driverInfo.name }]
              : []),
            ...(trip.driverInfo?.vehicle
              ? [{ label: t('coupon.transport.vehicle'), value: trip.driverInfo.vehicle }]
              : []),
            ...(trip.driverInfo?.licensePlate
              ? [{ label: t('coupon.transport.plate'), value: trip.driverInfo.licensePlate }]
              : []),
          ],
          note: trip.driverInfo?.name
            ? t('coupon.transport.showToDriver')
            : t('coupon.transport.vehiclePending'),
        });
      }
    } else if (transfer?.pickupTime) {
      coupons.push({
        id: 'transport-transfer',
        title: t('coupon.transport.title'),
        headline:
          transfer.kind === 'SHUTTLE'
            ? transfer.runSeq
              ? t('coupon.transport.shuttleRun', { seq: transfer.runSeq })
              : t('coupon.transport.shuttle')
            : t('coupon.transport.transfer'),
        sub: t(LEG_KEY[transfer.leg]),
        fields: [
          { label: t('coupon.transport.from'), value: station ?? '—' },
          {
            label: t('coupon.transport.pickUp'),
            value: formatFlightDateTime(transfer.pickupTime, station),
          },
        ],
        note: t('sheet.transport.meetingPoint'),
      });
    } else if (reservation) {
      // No ride, but there IS a hotel — which makes "nothing here" the single
      // most important thing this passenger can be told before they walk off.
      coupons.push({
        id: 'transport-pending',
        title: t('coupon.transport.title'),
        headline: t('coupon.transport.pendingScheduling'),
        fields: [],
        note: src.transportUnavailable
          ? t('coupon.transport.pendingUnavailable')
          : t('coupon.transport.pendingNoTime'),
      });
    }
  }

  if (kind === 'hotel-directions' && reservation) {
    coupons.push({
      id: 'directions',
      title: t('coupon.directions.title'),
      headline: reservation.hotelName ?? t('coupon.hotel.assignedHotel'),
      sub: station ? t('coupon.directions.nearAirport', { station }) : undefined,
      fields: [{ label: t('sheet.pnr'), value: group.pnrLabel }],
      note: t('coupon.directions.note'),
      link: src.mapsHref,
    });
  }

  return coupons;
}
